
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { CalendarIcon, Download } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

interface ExportSettingsProps {
  onSave: () => void;
}

export function ExportSettings({ onSave }: ExportSettingsProps) {
  const [defaultExportFormat, setDefaultExportFormat] = useState('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [dateFormat, setDateFormat] = useState('MM/dd/yyyy');
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [backupFrequency, setBackupFrequency] = useState('weekly');
  const [nextBackupDate, setNextBackupDate] = useState<Date>(new Date());
  
  const handleSaveSettings = () => {
    // This would update the export settings in the database
    console.log('Saving export settings:', {
      defaultExportFormat,
      includeHeaders,
      dateFormat,
      backupEnabled,
      backupFrequency,
      nextBackupDate: format(nextBackupDate, 'yyyy-MM-dd')
    });
    
    // Call onSave to trigger the toast notification
    onSave();
  };

  const handleRunBackupNow = () => {
    // This would trigger an immediate backup process
    console.log('Running backup now...');
    // In a real implementation, this would call a backend API
    // to initiate the backup process
    
    // Show a toast notification
    onSave();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Export Preferences</CardTitle>
          <CardDescription>
            Configure default settings for data exports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="export-format">Default Export Format</Label>
              <Select 
                value={defaultExportFormat} 
                onValueChange={setDefaultExportFormat}
              >
                <SelectTrigger id="export-format">
                  <SelectValue placeholder="Select export format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date-format">Date Format in Exports</Label>
              <Select 
                value={dateFormat} 
                onValueChange={setDateFormat}
              >
                <SelectTrigger id="date-format">
                  <SelectValue placeholder="Select date format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/dd/yyyy">MM/DD/YYYY</SelectItem>
                  <SelectItem value="dd/MM/yyyy">DD/MM/YYYY</SelectItem>
                  <SelectItem value="yyyy-MM-dd">YYYY-MM-DD</SelectItem>
                  <SelectItem value="MMMM d, yyyy">Month D, YYYY</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 mt-4">
            <Checkbox 
              id="include-headers" 
              checked={includeHeaders}
              onCheckedChange={(checked) => setIncludeHeaders(checked === true)}
            />
            <Label htmlFor="include-headers">
              Include headers in CSV and Excel exports
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Data Backup</CardTitle>
          <CardDescription>
            Configure settings for automatic data backups
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="backup-enabled" className="text-base">Automatic Backups</Label>
              <p className="text-sm text-muted-foreground">
                Enable regular automated backups of your data
              </p>
            </div>
            <Switch
              id="backup-enabled"
              checked={backupEnabled}
              onCheckedChange={setBackupEnabled}
            />
          </div>
          
          {backupEnabled && (
            <div className="space-y-4 pt-4 pl-4 border-l-2 border-muted mt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="backup-frequency">Backup Frequency</Label>
                  <Select 
                    value={backupFrequency} 
                    onValueChange={setBackupFrequency}
                  >
                    <SelectTrigger id="backup-frequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Next Scheduled Backup</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !nextBackupDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {nextBackupDate ? format(nextBackupDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={nextBackupDate}
                        onSelect={(date) => setNextBackupDate(date || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Backup Contents</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="backup-certificates" defaultChecked />
                    <Label htmlFor="backup-certificates">Certificates Data</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="backup-reports" defaultChecked />
                    <Label htmlFor="backup-reports">Report History</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="backup-templates" defaultChecked />
                    <Label htmlFor="backup-templates">Templates</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="backup-settings" defaultChecked />
                    <Label htmlFor="backup-settings">System Settings</Label>
                  </div>
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleRunBackupNow}
              >
                <Download className="mr-2 h-4 w-4" />
                Run Backup Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings}>
          Save Export Settings
        </Button>
      </div>
    </div>
  );
}
